
import React from 'react';

interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  label: string;
  error?: string;
  required?: boolean;
}

export const FormInput: React.FC<InputProps> = ({ label, error, required, className, ...props }) => (
  <div className={`flex flex-col gap-1 ${className}`}>
    <label className="text-sm font-semibold text-slate-700">
      {label} {required && <span className="text-red-500">*</span>}
    </label>
    <input
      {...props}
      className={`px-4 py-2 border rounded-lg focus:ring-2 focus:ring-emerald-500 bg-white text-slate-900 outline-none transition-all ${
        error ? 'border-red-500' : 'border-slate-300'
      }`}
    />
    {error && <span className="text-xs text-red-500 font-medium">{error}</span>}
  </div>
);

interface TextAreaProps extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
  label: string;
  error?: string;
  required?: boolean;
}

export const FormTextArea: React.FC<TextAreaProps> = ({ label, error, required, className, ...props }) => (
  <div className={`flex flex-col gap-1 ${className}`}>
    <label className="text-sm font-semibold text-slate-700">
      {label} {required && <span className="text-red-500">*</span>}
    </label>
    <textarea
      {...props}
      rows={props.rows || 3}
      className={`px-4 py-2 border rounded-lg focus:ring-2 focus:ring-emerald-500 bg-white text-slate-900 outline-none transition-all resize-none ${
        error ? 'border-red-500' : 'border-slate-300'
      }`}
    />
    {error && <span className="text-xs text-red-500 font-medium">{error}</span>}
  </div>
);

interface SelectProps extends React.SelectHTMLAttributes<HTMLSelectElement> {
  label: string;
  options: { value: string; label: string }[];
  error?: string;
  required?: boolean;
}

export const FormSelect: React.FC<SelectProps> = ({ label, options, error, required, className, ...props }) => (
  <div className={`flex flex-col gap-1 ${className}`}>
    <label className="text-sm font-semibold text-slate-700">
      {label} {required && <span className="text-red-500">*</span>}
    </label>
    <select
      {...props}
      className={`px-4 py-2 border rounded-lg focus:ring-2 focus:ring-emerald-500 bg-white text-slate-900 outline-none transition-all ${
        error ? 'border-red-500' : 'border-slate-300'
      }`}
    >
      <option value="">Select an option</option>
      {options.map((opt) => (
        <option key={opt.value} value={opt.value}>
          {opt.label}
        </option>
      ))}
    </select>
    {error && <span className="text-xs text-red-500 font-medium">{error}</span>}
  </div>
);

export const SectionTitle: React.FC<{ children: React.ReactNode; subtitle?: string }> = ({ children, subtitle }) => (
  <div className="mb-6 border-b border-slate-200 pb-2">
    <h3 className="text-xl font-bold text-emerald-800 uppercase tracking-tight">{children}</h3>
    {subtitle && <p className="text-sm text-slate-500 mt-1 italic">{subtitle}</p>}
  </div>
);

export const Card: React.FC<{ children: React.ReactNode; className?: string }> = ({ children, className }) => (
  <div className={`bg-white rounded-xl shadow-sm border border-slate-200 p-6 ${className}`}>
    {children}
  </div>
);

export const FileUpload: React.FC<{ 
  label: string; 
  onUpload: (base64: string) => void; 
  accept?: string; 
  value?: string | null;
  required?: boolean;
  error?: string;
}> = ({ label, onUpload, accept, value, required, error }) => {
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        onUpload(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  return (
    <div className="flex flex-col gap-1">
      <label className="text-sm font-semibold text-slate-700">
        {label} {required && <span className="text-red-500">*</span>}
      </label>
      <div className={`relative border-2 border-dashed rounded-lg p-4 hover:border-emerald-400 transition-colors bg-white flex flex-col items-center justify-center min-h-[120px] ${
        error ? 'border-red-500 bg-red-50' : 'border-slate-300'
      }`}>
        {value ? (
          <div className="flex flex-col items-center gap-2 text-center">
            <i className="fa-solid fa-circle-check text-emerald-500 text-3xl"></i>
            <span className="text-xs text-emerald-600 font-medium">File Uploaded Successfully</span>
            <button 
              type="button"
              onClick={() => onUpload('')}
              className="text-[10px] text-red-500 hover:underline"
            >
              Replace File
            </button>
          </div>
        ) : (
          <>
            <i className="fa-solid fa-cloud-arrow-up text-slate-400 text-3xl mb-2"></i>
            <span className="text-xs text-slate-500">Drag and drop or click to upload</span>
            <input 
              type="file" 
              accept={accept} 
              onChange={handleChange}
              className="absolute inset-0 opacity-0 cursor-pointer"
            />
          </>
        )}
      </div>
      {error && <span className="text-xs text-red-500 font-medium mt-1">{error}</span>}
    </div>
  );
};
